import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, STAGE_META, fmtMoney, fmtDate, fmtDateTime, formatError, isAdmin } from '../api';
import { useAuth } from '../AuthContext';
import { ArrowLeft, Banknote, Calendar, FileText, Plus, Save, CheckCircle2, Trash2 } from 'lucide-react';

export default function DealDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [bundle, setBundle] = useState(null);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // New entry forms
  const [showFollow, setShowFollow] = useState(false);
  const [followForm, setFollowForm] = useState({ due_at: '', type: 'call', notes: '' });
  const [showPay, setShowPay] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', type: 'deposit', method: 'Bank Transfer', notes: '' });
  const [activityBody, setActivityBody] = useState('');

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/deals/${id}`);
      setBundle(data);
      setEdit({
        stage: data.deal.stage,
        client_quote: data.deal.client_quote,
        estimator_cost: data.deal.estimator_cost,
        estimator_name: data.deal.estimator_name || '',
        turnaround_hours: data.deal.turnaround_hours || 48,
        owner_id: data.deal.owner_id || '',
        sales_rep_name: data.deal.sales_rep_name || '',
        notes: data.deal.notes || '',
        lost_reason: data.deal.lost_reason || '',
        next_touch_at: data.deal.next_touch_at ? new Date(data.deal.next_touch_at).toISOString().slice(0, 16) : '',
        // project info (often added later)
        project_name: data.deal.project_name || '',
        project_address: data.deal.project_address || '',
        project_size: data.deal.project_size || '',
        estimating_scope: data.deal.estimating_scope || '',
        contact_type: data.deal.contact_type || 'GC',
      });
    } catch (e) { setErr(formatError(e)); }
  }, [id]);

  useEffect(() => {
    reload();
    api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
    api.get('/meta').then((r) => setMeta(r.data)).catch(() => {});
  }, [reload]);

  if (err) return <div className="crm-card" style={{ color: '#9f1239' }}>{err}</div>;
  if (!bundle) return <div className="crm-card">Loading…</div>;

  const d = bundle.deal;
  const stageMeta = STAGE_META[d.stage] || { label: d.stage, color: '#64748b', light: '#e2e8f0' };
  const margin = Number(edit.client_quote || 0) - Number(edit.estimator_cost || 0);
  const userName = (uid) => users.find((u) => u.id === uid)?.name || '—';
  const paymentsTotal = bundle.payments.reduce((a, p) => a + Number(p.amount || 0), 0);

  const saveEdit = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        ...edit,
        client_quote: parseFloat(edit.client_quote || 0),
        estimator_cost: parseFloat(edit.estimator_cost || 0),
        turnaround_hours: parseInt(edit.turnaround_hours || 48, 10),
        owner_id: edit.owner_id || null,
        next_touch_at: edit.next_touch_at ? new Date(edit.next_touch_at).toISOString() : null,
      };
      await api.put(`/deals/${id}`, body);
      await reload();
    } catch (e) { setErr(formatError(e)); }
    setSaving(false);
  };

  const addFollow = async (e) => {
    e.preventDefault();
    try {
      await api.post('/followups', {
        deal_id: id,
        due_at: new Date(followForm.due_at).toISOString(),
        type: followForm.type,
        notes: followForm.notes,
      });
      setFollowForm({ due_at: '', type: 'call', notes: '' });
      setShowFollow(false);
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  const toggleFollow = async (f) => {
    try {
      await api.put(`/followups/${f.id}`, { completed: !f.completed });
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  const deleteFollow = async (f) => {
    if (!window.confirm('Delete this follow-up?')) return;
    try { await api.delete(`/followups/${f.id}`); reload(); } catch (e) { setErr(formatError(e)); }
  };

  const addPay = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments', {
        deal_id: id,
        amount: parseFloat(payForm.amount),
        type: payForm.type,
        method: payForm.method,
        notes: payForm.notes,
      });
      setPayForm({ amount: '', type: 'deposit', method: 'Bank Transfer', notes: '' });
      setShowPay(false);
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!activityBody.trim()) return;
    try {
      await api.post('/activities', { deal_id: id, body: activityBody, type: 'note' });
      setActivityBody('');
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div data-testid="crm-deal-detail">
      <Link to="/crm/pipeline" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--crm-indigo)', fontSize: 13, marginBottom: 10, textDecoration: 'none', fontWeight: 700 }}>
        <ArrowLeft size={14} /> Back to pipeline
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 14 }} className="dd-grid">

        {/* MAIN COLUMN */}
        <div>
          <div className="crm-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <span className="crm-pill" style={{ background: stageMeta.light, color: stageMeta.color, fontSize: 11 }}>
                  <span className="dot" style={{ background: stageMeta.color }} />{stageMeta.label}
                </span>
                <h2 style={{ margin: '8px 0 2px', fontSize: 22, color: 'var(--crm-ink)' }}>{d.company}</h2>
                <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: 0 }}>
                  {d.contact_name || '—'} · {d.contact_email || ''} · {d.contact_phone || ''}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                {admin ? (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--crm-muted)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Margin</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: margin > 0 ? 'var(--crm-emerald)' : 'var(--crm-muted)' }}>{fmtMoney(margin)}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--crm-muted)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Quote</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--crm-indigo)' }}>{fmtMoney(edit.client_quote)}</div>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginTop: 14, color: 'var(--crm-muted)', fontSize: 13 }}>
              <b style={{ color: 'var(--crm-ink)' }}>{d.contact_type}</b> · {d.source}
              {' · '}
              {d.project_name ? <>{d.project_name}{d.project_address ? ` · ${d.project_address}` : ''}</> : <em>Project info pending</em>}
              {d.sales_rep_name && <> · Rep: <b style={{ color: 'var(--crm-ink)' }}>{d.sales_rep_name}</b></>}
            </div>
          </div>

          <div className="crm-card">
            <h3>Lead details</h3>
            <div className="crm-grid-3">
              <div>
                <label className="crm-label">Stage</label>
                <select className="crm-select" value={edit.stage} onChange={(e) => setEdit({ ...edit, stage: e.target.value })} data-testid="dd-stage">
                  {(meta?.stages || []).map((s) => <option key={s} value={s}>{STAGE_META[s]?.label || s}</option>)}
                </select>
              </div>
              <div>
                <label className="crm-label">Sales rep (name)</label>
                <input
                  className="crm-input"
                  value={edit.sales_rep_name}
                  onChange={(e) => setEdit({ ...edit, sales_rep_name: e.target.value })}
                  placeholder="e.g. Sarah Johnson"
                  data-testid="dd-sales-rep-name"
                />
              </div>
              <div>
                <label className="crm-label">Contractor type</label>
                <select className="crm-select" value={edit.contact_type} onChange={(e) => setEdit({ ...edit, contact_type: e.target.value })}>
                  {(meta?.contact_types || []).map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="crm-label">Client quote ($)</label>
                <input className="crm-input" value={edit.client_quote} onChange={(e) => setEdit({ ...edit, client_quote: e.target.value })} data-testid="dd-client-quote" />
              </div>
              {admin && (
                <div>
                  <label className="crm-label">Estimator cost ($)</label>
                  <input className="crm-input" value={edit.estimator_cost} onChange={(e) => setEdit({ ...edit, estimator_cost: e.target.value })} data-testid="dd-estimator-cost" />
                </div>
              )}
              <div>
                <label className="crm-label">Turnaround (hrs)</label>
                <input className="crm-input" value={edit.turnaround_hours} onChange={(e) => setEdit({ ...edit, turnaround_hours: e.target.value })} />
              </div>
            </div>

            {edit.stage === 'nurture' && (
              <div style={{ marginTop: 10, background: '#cffafe', padding: 12, borderRadius: 10 }}>
                <label className="crm-label">Next check-in date</label>
                <input
                  type="datetime-local"
                  className="crm-input"
                  value={edit.next_touch_at}
                  onChange={(e) => setEdit({ ...edit, next_touch_at: e.target.value })}
                />
                <p style={{ fontSize: 11, color: 'var(--crm-muted)', margin: '6px 0 0' }}>
                  This lead said "maybe later" — set a reminder to circle back when the time is right.
                </p>
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <label className="crm-label">Notes / interest level</label>
              <textarea className="crm-textarea" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
            </div>

            {edit.stage === 'lost' && (
              <div style={{ marginTop: 10 }}>
                <label className="crm-label">Lost reason</label>
                <input className="crm-input" value={edit.lost_reason} onChange={(e) => setEdit({ ...edit, lost_reason: e.target.value })} />
              </div>
            )}
            <div style={{ marginTop: 14, textAlign: 'right' }}>
              <button className="crm-btn" onClick={saveEdit} disabled={saving} data-testid="dd-save"><Save size={14} /> {saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>

          {/* Project Information — filled in once estimator is assigned */}
          <div className="crm-card">
            <h3>
              Project information
              {!edit.project_name && !edit.project_address && !edit.project_size && (
                <span className="crm-pill" style={{ background: 'var(--crm-amber-soft)', color: '#92400e', marginLeft: 8, fontSize: 10 }}>
                  Pending
                </span>
              )}
            </h3>

            {/* Empty state prompt when reaching the estimator stage */}
            {!edit.project_name && !edit.project_size && ['estimator_assigned', 'drawings_received', 'quote_sent'].includes(edit.stage) && (
              <div style={{ background: 'var(--crm-amber-soft)', borderLeft: '3px solid var(--crm-amber)', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                <b>Time to fill in project details!</b> The lead has reached the estimator stage —
                add the project name, address, square footage and scope below so the estimate is properly tagged.
              </div>
            )}

            <p style={{ fontSize: 12, color: 'var(--crm-muted)', margin: '0 0 12px' }}>
              Leave these blank for "no project yet" leads. Fill them in once the contractor sends drawings.
            </p>

            <div className="crm-grid-2">
              <div>
                <label className="crm-label">Project name</label>
                <input className="crm-input" value={edit.project_name} onChange={(e) => setEdit({ ...edit, project_name: e.target.value })} placeholder="Tysons Office TI – Building C" data-testid="dd-project-name" />
              </div>
              <div>
                <label className="crm-label">Project address</label>
                <input className="crm-input" value={edit.project_address} onChange={(e) => setEdit({ ...edit, project_address: e.target.value })} placeholder="1750 Tysons Blvd, McLean, VA" data-testid="dd-project-address" />
              </div>
              <div>
                <label className="crm-label">Approx. SF / size</label>
                <input className="crm-input" value={edit.project_size} onChange={(e) => setEdit({ ...edit, project_size: e.target.value })} placeholder="28,000 SF · 3 floors" data-testid="dd-project-size" />
              </div>
              <div>
                <label className="crm-label">Estimating scope (trade / divisions)</label>
                <input className="crm-input" value={edit.estimating_scope} onChange={(e) => setEdit({ ...edit, estimating_scope: e.target.value })} placeholder="All 16 CSI Divisions / Electrical only / etc." data-testid="dd-project-scope" />
              </div>
              <div>
                <label className="crm-label">Estimator name</label>
                <input className="crm-input" value={edit.estimator_name} onChange={(e) => setEdit({ ...edit, estimator_name: e.target.value })} placeholder="Outside estimator's name" />
              </div>
            </div>
            <div style={{ marginTop: 14, textAlign: 'right' }}>
              <button className="crm-btn" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save project info'}</button>
            </div>
          </div>

          {/* Activity log */}
          <div className="crm-card">
            <h3>Activity & notes</h3>
            <form onSubmit={addActivity} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="crm-input" placeholder="Add a quick note (Enter to save)" value={activityBody} onChange={(e) => setActivityBody(e.target.value)} />
              <button className="crm-btn" type="submit">Add</button>
            </form>
            <div>
              {(bundle.activities || []).length === 0
                ? <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>No activity yet.</p>
                : bundle.activities.map((a) => (
                  <div key={a.id} style={{ borderLeft: '3px solid #c7d2fe', padding: '6px 10px', marginBottom: 8, background: '#f8fafc', borderRadius: 6 }}>
                    <div style={{ fontSize: 13, color: 'var(--crm-ink)' }}>{a.body}</div>
                    <div style={{ fontSize: 11, color: 'var(--crm-muted)', marginTop: 2 }}>{userName(a.user_id)} · {fmtDateTime(a.created_at)}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* SIDE COLUMN */}
        <div>
          {/* Follow-ups */}
          <div className="crm-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}><Calendar size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Follow-ups</h3>
              <button className="crm-btn sm" onClick={() => setShowFollow(!showFollow)}><Plus size={12} /> Add</button>
            </div>
            {showFollow && (
              <form onSubmit={addFollow} style={{ background: '#f8fafc', padding: 10, borderRadius: 8, marginBottom: 10 }}>
                <label className="crm-label">When</label>
                <input type="datetime-local" required className="crm-input" value={followForm.due_at} onChange={(e) => setFollowForm({ ...followForm, due_at: e.target.value })} />
                <div style={{ marginTop: 6 }}>
                  <label className="crm-label">Type</label>
                  <select className="crm-select" value={followForm.type} onChange={(e) => setFollowForm({ ...followForm, type: e.target.value })}>
                    <option value="call">Call</option><option value="email">Email</option>
                    <option value="meeting">Meeting</option><option value="task">Task</option>
                  </select>
                </div>
                <div style={{ marginTop: 6 }}>
                  <label className="crm-label">Notes</label>
                  <input className="crm-input" value={followForm.notes} onChange={(e) => setFollowForm({ ...followForm, notes: e.target.value })} placeholder="Confirm drawings sent" />
                </div>
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <button className="crm-btn sm" type="submit">Save follow-up</button>
                </div>
              </form>
            )}
            {(bundle.followups || []).length === 0
              ? <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>No follow-ups scheduled.</p>
              : bundle.followups.map((f) => {
                const overdue = !f.completed && new Date(f.due_at) < new Date();
                return (
                  <div key={f.id} style={{
                    borderLeft: `3px solid ${f.completed ? '#10b981' : overdue ? '#f43f5e' : '#6366f1'}`,
                    padding: '8px 10px', marginBottom: 6, background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: overdue ? '#9f1239' : 'var(--crm-ink)' }}>
                        {fmtDateTime(f.due_at)} {overdue && '· OVERDUE'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--crm-muted)' }}>{f.type.toUpperCase()} · {f.notes}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button onClick={() => toggleFollow(f)} className="crm-btn ghost sm" title={f.completed ? 'Mark incomplete' : 'Mark done'}>
                        <CheckCircle2 size={12} color={f.completed ? '#10b981' : '#94a3b8'} />
                      </button>
                      <button onClick={() => deleteFollow(f)} className="crm-btn ghost sm" title="Delete"><Trash2 size={12} color="#94a3b8" /></button>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Payments */}
          <div className="crm-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}><Banknote size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Payments</h3>
              <button className="crm-btn sm success" onClick={() => setShowPay(!showPay)}><Plus size={12} /> Log payment</button>
            </div>
            <div style={{ background: 'var(--crm-emerald-soft)', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#065f46', fontWeight: 700, letterSpacing: 0.1 }}>RECEIVED</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#065f46' }}>{fmtMoney(paymentsTotal)}</div>
            </div>
            {showPay && (
              <form onSubmit={addPay} style={{ background: '#f8fafc', padding: 10, borderRadius: 8, marginBottom: 10 }}>
                <div className="crm-grid-2">
                  <div><label className="crm-label">Amount</label><input required className="crm-input" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="2325" /></div>
                  <div>
                    <label className="crm-label">Type</label>
                    <select className="crm-select" value={payForm.type} onChange={(e) => setPayForm({ ...payForm, type: e.target.value })}>
                      <option value="deposit">50% Deposit</option>
                      <option value="final">Final 50%</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 6 }}>
                  <label className="crm-label">Method</label>
                  <input className="crm-input" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} />
                </div>
                <div style={{ marginTop: 6 }}>
                  <label className="crm-label">Notes</label>
                  <input className="crm-input" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
                </div>
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <button className="crm-btn sm success" type="submit">Save payment</button>
                </div>
              </form>
            )}
            {(bundle.payments || []).length === 0
              ? <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>No payments recorded.</p>
              : bundle.payments.map((p) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #e2e8f0', fontSize: 12.5 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{fmtMoney(p.amount)} · {p.type}</div>
                    <div style={{ color: 'var(--crm-muted)', fontSize: 11 }}>{fmtDate(p.received_at)} · {p.method}</div>
                  </div>
                </div>
              ))}
          </div>

          {/* Drawings */}
          {d.attachments && d.attachments.length > 0 && (
            <div className="crm-card">
              <h3><FileText size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Drawings</h3>
              {d.attachments.map((a) => (
                <div key={a} style={{ fontSize: 12.5, padding: '4px 0', borderBottom: '1px dashed #e2e8f0', color: 'var(--crm-muted)' }}>
                  📎 {a.split('__').slice(1).join('__')}
                </div>
              ))}
              <p style={{ fontSize: 11, color: 'var(--crm-muted)', marginTop: 8 }}>Files saved on server. Forward them to your estimator.</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 980px) { .dd-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}
